import { Label } from "@/components/ui/label";
import { Controller } from "react-hook-form";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { CfValueInput } from "@/features/customField/components/CfValueInput";
import { cfTextValueProblem } from "@/features/customField/utils/cfValueTypes";
import { useRequiredFieldMark } from "@/features/leads/requirements/requiredFieldMarks";
import { RequiredStar } from "@/features/leads/requirements/requiredFieldMarks.ui";

interface PrefrenceFormattedFieldProps {
  val: any;
  index: number;
  control: any;
  errors: any;
  readOnly?: boolean;
}

/**
 * A Number / Percentage / Date custom field on a form — the formatted twin of
 * `PrefrenceTextField`: same label, requirement star, grid slot and
 * `customInputScopes.<index>` binding (`{ customInputId, optionIds: [],
 * textValue }`), with the type's own input. A date can't be picked past the
 * field's limit; a number or percentage that won't save says so here.
 */
export default function PrefrenceFormattedField({
  val,
  index,
  control,
  errors,
  readOnly = false,
}: PrefrenceFormattedFieldProps) {
  // Stage-requirement star (null outside a marks provider → no change).
  const required = useRequiredFieldMark("custom", val?.id);
  return (
    <div
      key={val.id}
      className="flex flex-col gap-1.5 [&:last-child:nth-child(odd)]:col-span-2"
    >
      <Label
        htmlFor={`customInputScopes-${index}`}
        className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35]"
      >
        {getCfDisplayName(val)}
        <RequiredStar mark={required} className="-ml-1.5" />
      </Label>

      <Controller
        name={`customInputScopes.${index}`}
        control={control}
        render={({ field }) => {
          const textValue = field.value?.textValue ?? "";
          // Dates come from the picker, which already stops at the limit.
          const problem =
            val?.type === "DATE" ? null : cfTextValueProblem(val, textValue);
          return (
            <>
              <CfValueInput
                id={`customInputScopes-${index}`}
                field={val}
                value={textValue}
                disabled={readOnly}
                onChange={(next) =>
                  field.onChange({
                    customInputId: val.id,
                    optionIds: [],
                    textValue: next,
                  })
                }
                invalid={Boolean(errors?.customInputScopes?.[index]) || Boolean(problem)}
                className="bg-[#fcfcfc] border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm w-full"
              />
              {problem && <p className="text-[12px] text-red-500">{problem}</p>}
            </>
          );
        }}
      />
    </div>
  );
}
